import { z } from 'zod';

// Direct import (not the barrel) to avoid pulling the heavy DateRangeFilter
// → @mui/x-date-pickers dep into the users bundle.
import type { StatusOption } from '../../../components/filters/StatusMultiSelect';

/**
 * URL filter schema for the users list (AUT-124). Mirrors the backend
 * `ListAdminUsersQuerySchema` enums so the SPA never sends a value the
 * server will reject. Every key has a `.catch(default)` so a malformed /
 * absent query param degrades to a safe default instead of throwing —
 * `useUrlFilters` parses the raw query string straight through this.
 */

export const USER_ROLES = ['customer', 'inspector', 'admin'] as const;
export const USER_STATUSES = ['active', 'suspended', 'banned', 'deleted'] as const;
export const USER_KYC_STATUSES = [
  'unverified',
  'profile_filled',
  'idcheck_pending',
  'idcheck_passed',
  'idcheck_failed',
  'bank_pending',
  'active',
  'reverification_required',
  'suspended',
  'banned',
] as const;

/** Min free-text search length (mirrors backend MIN_SEARCH_LENGTH). */
export const MIN_SEARCH_LENGTH = 3;

const roleSchema = z.enum(USER_ROLES).optional().catch(undefined);

/** `status=active,suspended` → string[] of valid statuses (empty on junk). */
const statusSchema = z
  .string()
  .optional()
  .catch(undefined)
  .transform((raw) =>
    (raw ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is (typeof USER_STATUSES)[number] =>
        (USER_STATUSES as readonly string[]).includes(s),
      ),
  );

const kycSchema = z.enum(USER_KYC_STATUSES).optional().catch(undefined);

const qSchema = z.string().optional().catch(undefined);

export const userFiltersSchema = z.object({
  role: roleSchema,
  status: statusSchema,
  kycStatus: kycSchema,
  q: qSchema,
});

export type UserFilters = z.infer<typeof userFiltersSchema>;

/** Status filter options (multi-select) — labels resolved via i18n keys. */
export const STATUS_OPTIONS: StatusOption[] = USER_STATUSES.map((value) => ({
  value,
  labelKey: `users.status.${value}`,
}));

/** Role filter options (single-select). */
export const ROLE_OPTIONS = USER_ROLES.map((value) => ({
  value,
  labelKey: `users.role.${value}`,
}));

/** KYC filter options (single-select). */
export const KYC_OPTIONS = USER_KYC_STATUSES.map((value) => ({
  value,
  labelKey: `users.kyc.${value}`,
}));

/** True when any managed filter is active (drives the Clear-filters link). */
export const hasActiveFilters = (filters: UserFilters): boolean =>
  Boolean(filters.role) ||
  (filters.status?.length ?? 0) > 0 ||
  Boolean(filters.kycStatus) ||
  Boolean(filters.q && filters.q.length > 0);
